import React, { memo, ReactNode } from 'react';
import { Card, Bleed, Box, BlockStack, Button, Icon, SkeletonBodyText, Text, ButtonProps } from '@shopify/polaris';
import { ImageIcon, XIcon } from '@shopify/polaris-icons';
import MediaMetadata from './MediaMetadata';
import { MediaAssetStatus, ICardMediaAsset, PlayAppMediaAssetStatus } from '@/types/media';
import MediaAssetCardContent from './MediaAssetCardContent';
import MediaAssetCardActions from './MediaAssetCardActions';
import { cn } from '@/lib/utils';

interface MediaAssetCardProps {
  media?: ICardMediaAsset;
  hideUseAction?: boolean;
  size?: 'medium' | 'large' | 'small';
  aspectRatio?: 'square' | 'portrait';
  hideMetadata?: boolean;
  selected?: boolean;
  onSelect?: (media: ICardMediaAsset, selected: boolean) => void;
  metadataComponent?: ReactNode;
  onClose?: (media?: ICardMediaAsset) => void;
  disableDeselect?: boolean;
  loading?: boolean;
  failed?: boolean;
  inGrid?: boolean;
  showProgressBannerWarning?: boolean;
  actionButtonSize?: ButtonProps['size'];
}

const MediaAssetCard = ({
  media,
  hideUseAction = false,
  size = 'large',
  aspectRatio = 'portrait',
  hideMetadata = false,
  selected = false,
  onSelect,
  metadataComponent,
  onClose,
  disableDeselect = false,
  loading = false,
  failed = false,
  inGrid = false,
  showProgressBannerWarning,
  actionButtonSize = 'large',
}: MediaAssetCardProps) => {
  const handleClick = () => {
    if (onSelect && media) {
      onSelect(media, !selected);
    }
  };

  if (loading) {
    return (
      <Box maxWidth="300px">
        <Card>
          <Box borderInlineStartWidth={selected ? '050' : '0'} borderColor={selected ? 'border-focus' : 'transparent'}>
            <div
              className={cn(
                'relative flex flex-col justify-between overflow-hidden',
                size === 'large' && 'h-[310px]',
                size === 'medium' && 'h-[270px]',
                size === 'small' && 'h-[220px]',
                aspectRatio === 'square' ? 'aspect-square' : 'aspect-[9/16]'
              )}
            >
              <SkeletonBodyText lines={size === 'large' ? 20 : size === 'medium' ? 15 : 10} />
            </div>
          </Box>
        </Card>
      </Box>
    );
  }
  const showActions = !hideUseAction;
  const areActionsDisabled =
    !media ||
    ![MediaAssetStatus.DONE, PlayAppMediaAssetStatus.Done].includes(
      media?.status as MediaAssetStatus | PlayAppMediaAssetStatus
    );
  const showMetadata = !hideMetadata && media;

  return (
    <Box maxWidth="300px">
      <Card>
        <Bleed marginInline="400" marginBlock="400">
          <Box borderInlineStartWidth={selected ? '050' : '0'} borderColor={selected ? 'border-focus' : 'transparent'}>
            <div
              className={cn(
                'relative flex flex-col justify-between overflow-hidden',
                size === 'large' && 'h-[310px]',
                size === 'medium' && 'h-[270px]',
                size === 'small' && 'h-[220px]',

                aspectRatio === 'square' ? 'aspect-square' : 'aspect-[9/16]'
              )}
            >
              {onClose && (
                <div
                  className="absolute right-2 top-2 z-10 cursor-pointer rounded-md bg-black/20 p-1 text-white"
                  onClick={() => onClose(media)}
                >
                  <Icon source={XIcon} tone="inherit" />
                </div>
              )}
              {failed ? (
                <div className="flex h-full w-full items-center justify-center text-center">
                  <Text tone="critical" as="p" variant="bodyMd">
                    Failed to generate image
                  </Text>
                </div>
              ) : (
                <>
                  {media ? (
                    <MediaAssetCardContent
                      media={media}
                      size={size}
                      inGrid={inGrid}
                      showProgressBannerWarning={showProgressBannerWarning}
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-zinc-200">
                      <ImageIcon className="h-16 w-16 fill-zinc-500" />
                    </div>
                  )}
                </>
              )}
            </div>
          </Box>
          {!failed && (
            <Box padding={size === 'small' ? '200' : '400'} background="bg-surface-secondary">
              {(showMetadata || showActions) && (
                <BlockStack gap="400">
                  {showMetadata &&
                    (metadataComponent ? (
                      metadataComponent
                    ) : (
                      <Box>
                        <MediaMetadata media={media} hideFullScreen={!showActions} />
                      </Box>
                    ))}

                  {showActions && (
                    <MediaAssetCardActions media={media} disabled={areActionsDisabled} buttonSize={actionButtonSize} />
                  )}
                </BlockStack>
              )}
              {!!onSelect && (
                <BlockStack inlineAlign="center">
                  <Button onClick={handleClick} disabled={selected && disableDeselect}>
                    {selected ? (disableDeselect ? 'Selected' : 'Deselect') : 'Select'}
                  </Button>
                </BlockStack>
              )}
            </Box>
          )}
        </Bleed>
      </Card>
    </Box>
  );
};

export default memo(MediaAssetCard);
